import { createCodedError } from '~/common/errors/CodedError';
import { EsignatureType } from '~/enums/EsignatureType';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { EsignatureIntegrationType } from '~/features/admin/ESignature/types';
import { EsignatureTokenStatusValue } from '~/features/ticket/types/esignature';
import { JsonApiErrorResponse } from '~/redux/api/ApiTypes';
import { testIsAxiosError } from '~/redux/api/utils';
import { esignature } from '~/services/serviceInstances';
import { Nullable } from '~/types/Nullable';

type Request = {
  ticketId?: string;
};

type ServerResponse = {
  data: {
    type: string;
    id: string;
    attributes: {
      integration_type: string;
      vendor: string;
      account_token: Nullable<EsignatureTokenStatusValue>;
      envelope_token: Nullable<EsignatureTokenStatusValue>;
    };
  };
};

export type EsignatureTokenStatus = {
  integrationType: EsignatureIntegrationType;
  providerType: EsignatureType;
  accountToken: Nullable<EsignatureTokenStatusValue>;
  envelopeToken: Nullable<EsignatureTokenStatusValue>;
};

export const getTokenStatus = async (
  request: Request,
): Promise<Nullable<EsignatureTokenStatus>> => {
  try {
    const response: ServerResponse = await esignature.get(
      `tokens/status${
        request.ticketId ? `?ticket_id=${request.ticketId}` : ''
      }`,
    );

    return mapResponse(response);
  } catch (error) {
    if (
      testIsAxiosError(error) &&
      error.response?.status === HttpStatusCodeType.NotFound
    ) {
      // if there is no connected e-signature integration, server returns 404
      return null;
    }

    if (testIsAxiosError<JsonApiErrorResponse>(error) && error.response) {
      const errorResponse = error.response.data.errors[0];
      throw createCodedError({
        message: errorResponse.detail ?? errorResponse.title,
        statusCode: errorResponse.status,
        title: errorResponse.title,
      });
    }

    throw error;
  }
};

const mapResponse = (serverResponse: ServerResponse): EsignatureTokenStatus => {
  return {
    integrationType:
      serverResponse.data.attributes.integration_type.toLowerCase() as EsignatureIntegrationType,
    providerType:
      serverResponse.data.attributes.vendor.toLowerCase() as EsignatureType,
    accountToken: serverResponse.data.attributes.account_token,
    envelopeToken: serverResponse.data.attributes.envelope_token,
  };
};
